import { Shield, BookOpen, Heart, Target } from "lucide-react";

const COMMUNITY_IMG = "https://images.unsplash.com/photo-1776179806370-8c5279456d54?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjaHVyY2glMjBjb21tdW5pdHklMjBidWlsZGluZ3xlbnwwfHx8fDE3NzYzNTIxMDF8MA&ixlib=rb-4.1.0&q=85";

const values = [
  {
    icon: Shield,
    title: "Integrity",
    desc: "We operate with transparency, accountability, and adherence to established standards.",
  },
  {
    icon: Heart,
    title: "Compassion",
    desc: "Every family we serve is treated with genuine care, understanding, and respect.",
  },
  {
    icon: BookOpen,
    title: "Excellence",
    desc: "We maintain high standards in record-keeping, communication, and organizational support.",
  },
  {
    icon: Target,
    title: "Purpose",
    desc: "Everything we do is driven by our mission to serve families and churches through faith-based education.",
  },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Header */}
      <section className="bg-[#1C2E4A] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">
            About Us
          </p>
          <h1
            data-testid="about-headline"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-3xl"
          >
            Who We Are
          </h1>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">
              Our Story
            </p>
            <h2
              data-testid="about-story-heading"
              className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1C2E4A] mb-6"
            >
              A Church Built on Service
            </h2>
            <div className="space-y-4 text-[#595959] leading-relaxed">
              <p>
                ACS Virtual Church was established with a clear purpose: to serve
                families and churches who need a structured, faith-based
                educational framework. We recognized that many homeschool families
                and small congregations needed organizational support that honored
                their values.
              </p>
              <p>
                As a virtual church, we are not bound by a physical building. Our
                ministry operates entirely online, allowing us to serve families
                and churches across the country. We build complete church school
                systems — enrollment portals, student management,
                and admin dashboards — so churches can launch and run their own
                independent school programs.
              </p>
              <p>
                We are not a software company or a startup. We are a church that
                has developed systems and processes to help other churches and
                families build strong, independent educational programs.
              </p>
            </div>
          </div>
          <div className="rounded-xl overflow-hidden shadow-lg">
            <img
              src={COMMUNITY_IMG}
              alt="Church Community"
              className="w-full h-80 lg:h-[420px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* Values */}
      <section data-testid="about-values-section" className="py-20 md:py-28 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">
              Our Values
            </p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1C2E4A]">
              What Guides Us
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {values.map((v, i) => (
              <div
                key={v.title}
                data-testid={`value-card-${i}`}
                className="bg-white rounded-xl border border-[#E5E2DC] p-8 hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-lg bg-[#1C2E4A]/5 flex items-center justify-center mb-5">
                  <v.icon className="w-5 h-5 text-[#1C2E4A]" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-[#1C2E4A] mb-2">
                  {v.title}
                </h3>
                <p className="text-sm text-[#595959] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
